import { Component } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import logger from "@/lib/logger";

/**
 * Top-level React error boundary. Catches render-time exceptions in any
 * descendant and surfaces a graceful fallback instead of a blank screen.
 * Errors are mirrored to /api/client-logs via the shared logger.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    try {
      logger.error("react-error-boundary", error?.message || String(error), {
        stack: error?.stack,
        componentStack: info?.componentStack,
        scope: this.props.scope || "app",
      });
    } catch (_e) {
      // logger must never break the fallback
    }
  }

  handleReload = () => {
    // Hard reload — safest recovery path for an unknown render-time crash.
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-screen bg-[var(--bg-deep)] text-[var(--text-primary)] flex items-center justify-center px-6"
        data-testid="error-boundary-fallback"
      >
        <div
          className="max-w-md w-full bg-[var(--bg-canvas)] border border-[var(--border-hover)] p-8 text-left"
          style={{ borderRadius: 4 }}
        >
          <div
            className="w-11 h-11 flex items-center justify-center bg-[#E82127]/12 border border-[#E82127]/30 mb-5"
            style={{ borderRadius: 999 }}
          >
            <AlertTriangle className="w-5 h-5 text-[#FF5A5F]" strokeWidth={1.75} />
          </div>
          <div className="label-tiny mb-2">Something Broke</div>
          <h2 className="font-display text-2xl font-light tracking-tighter text-[var(--text-primary)] mb-3">
            The dashboard hit an unexpected error.
          </h2>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-6">
            Your data is safe. We&apos;ve recorded what happened. Reload the page to get back to
            your dashboard.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="tap inline-flex items-center gap-2 bg-[var(--bg-inverse)] text-[var(--text-inverse)] px-4 py-2 text-sm font-medium hover:bg-[var(--bg-inverse-hover)] transition-colors"
            style={{ borderRadius: 2 }}
            data-testid="error-boundary-reload-btn"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={2} />
            Reload Dashboard
          </button>
        </div>
      </div>
    );
  }
}
